import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { caseAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  Scale,
  Loader2,
  BookOpen,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';

// Removed static domains and commonFacts in favor of translation hook

export default function CasePage() {
  const [domain, setDomain] = useState('');
  const [selectedFacts, setSelectedFacts] = useState([]);
  const [narrative, setNarrative] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const domainsConfig = t('case.domainsConfig');
  const factsConfig = t('case.factsConfig');

  const domains = Array.isArray(domainsConfig) ? domainsConfig : [];
  const commonFacts = typeof factsConfig === 'object' && factsConfig !== null ? factsConfig : {};

  const handleFactToggle = (fact) => {
    setSelectedFacts(prev =>
      prev.includes(fact)
        ? prev.filter(f => f !== fact)
        : [...prev, fact]
    );
  };

  const handleAnalyze = async () => {
    if (!domain) {
      toast.error('Please select a domain');
      return;
    }

    setLoading(true);
    try {
      const response = await caseAPI.analyze(domain, selectedFacts, narrative);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze case');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDomain('');
    setSelectedFacts([]);
    setNarrative('');
    setAnalysis(null);
  };

  const availableFacts = domain ? commonFacts[domain] || [] : [];

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="case-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <Scale className="h-4 w-4" />
          <span>Case Mode</span>
        </div>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
          {t('case.title')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('case.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          <Card data-testid="case-input">
            <CardHeader>
              <CardTitle className="text-lg">{t('case.buildCase.title')}</CardTitle>
              <CardDescription>{t('case.buildCase.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Domain Selection */}
              <div className="space-y-2">
                <Label>{t('case.buildCase.domain')}</Label>
                <Select value={domain} onValueChange={setDomain}>
                  <SelectTrigger data-testid="domain-select">
                    <SelectValue placeholder={t('case.buildCase.selectDomain')} />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (
                      <SelectItem key={d.value} value={d.value}>
                        <div>
                          <div className="font-medium">{d.label}</div>
                          <div className="text-xs text-muted-foreground">{d.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Facts Selection */}
              {domain && (
                <div className="space-y-2">
                  <Label>{t('case.buildCase.relevantFacts')}</Label>
                  <div className="space-y-2">
                    {availableFacts.map((fact, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <Checkbox
                          id={`fact-${i}`}
                          checked={selectedFacts.includes(fact)}
                          onCheckedChange={() => handleFactToggle(fact)}
                          data-testid={`fact-checkbox-${i}`}
                        />
                        <Label htmlFor={`fact-${i}`} className="text-sm cursor-pointer">
                          {fact}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Narrative */}
              <div className="space-y-2">
                <Label>{t('case.buildCase.additionalNarrative')}</Label>
                <Textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  placeholder={t('case.buildCase.narrativePlaceholder')}
                  className="min-h-[100px]"
                  data-testid="narrative-input"
                />
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <Button
                  onClick={handleAnalyze}
                  disabled={!domain || loading}
                  className="flex-1"
                  data-testid="analyze-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('case.actions.analyzing')}
                    </>
                  ) : (
                    <>
                      <Scale className="h-4 w-4 mr-2" />
                      {t('case.actions.analyze')}
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleReset} data-testid="reset-button">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Important Note */}
          <Card className="border-amber-500/50 bg-amber-500/5">
            <CardContent className="pt-4">
              <div className="flex items-start space-x-2 text-sm">
                <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium">{t('case.notLegalAdvice.title')}</p>
                  <p className="text-muted-foreground">
                    {t('case.notLegalAdvice.description')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results Section */}
        <div>
          {analysis ? (
            <Card data-testid="analysis-results">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{t('case.results.title')}</CardTitle>
                    <CardDescription className="capitalize">{analysis.domain} domain</CardDescription>
                  </div>
                  <Badge variant="outline" className="capitalize">{analysis.domain}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-6">
                    {/* Triggered Principles */}
                    <div>
                      <h4 className="font-medium mb-3 flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        {t('case.results.triggeredPrinciples')}
                      </h4>
                      <div className="space-y-2">
                        {analysis.triggered_principles.map((p, i) => (
                          <div key={i} className="p-3 rounded-lg bg-muted/50">
                            <div className="font-medium">{p.name}</div>
                            <div className="text-sm text-muted-foreground">{p.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Applicable Articles */}
                    <div>
                      <h4 className="font-medium mb-3 flex items-center">
                        <BookOpen className="h-4 w-4 mr-2" />
                        {t('case.results.applicableArticles')}
                      </h4>
                      <div className="space-y-2">
                        {analysis.applicable_articles.map((a, i) => (
                          <Link
                            key={i}
                            to={`/article/${a.number}`}
                            className="block p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                            data-testid={`result-article-${a.number}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <Badge variant="outline" className="mb-1">Article {a.number}</Badge>
                                <div className="font-medium">{a.title}</div>
                                <div className="text-sm text-muted-foreground line-clamp-2">{a.content}</div>
                              </div>
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Thresholds */}
                    <div>
                      <h4 className="font-medium mb-3">{t('case.results.thresholds')}</h4>
                      <ul className="space-y-1">
                        {analysis.thresholds.map((t, i) => (
                          <li key={i} className="text-sm flex items-start space-x-2">
                            <span className="text-accent">•</span>
                            <span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Separator />

                    {/* Four Scales */}
                    <div>
                      <h4 className="font-medium mb-3 flex items-center">
                        <Scale className="h-4 w-4 mr-2" />
                        {t('case.results.fourScale')}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(analysis.four_scales).map(([key, value]) => (
                          <div key={key} className="scale-item">
                            <div className="scale-label capitalize">{key.replace('_', ' ')}</div>
                            <div className="text-sm">{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Possible Outcomes */}
                    <div>
                      <h4 className="font-medium mb-3">{t('case.results.outcomes')}</h4>
                      <ul className="space-y-1">
                        {analysis.possible_outcomes.map((o, i) => (
                          <li key={i} className="text-sm flex items-start space-x-2">
                            <span className="text-accent">•</span>
                            <span>{o}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Separator />

                    {/* Conclusion */}
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <h4 className="font-medium mb-2">{t('case.results.conclusion')}</h4>
                      <p className="text-sm">{analysis.conclusion}</p>
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full min-h-[400px] flex items-center justify-center">
              <CardContent className="text-center text-muted-foreground">
                <Scale className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium mb-2">{t('case.results.noAnalysis')}</h3>
                <p className="text-sm max-w-xs mx-auto">
                  {t('case.results.noAnalysisDesc')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
